import { useEffect, useState } from "react";
import style from "../css/AdminLogin.module.css";
import {
  getHotDealsOrderData,
  updateHotDealsOrderData,
} from "../../../utils/api";

const statusOptions = [
  "PLACED",
  "CONFIRMED",
  "SHIPPED",
  "DELIVERED",
  "CANCELLED"
];

function AdminDashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [filteredOrders, setFilteredOrders] = useState([]);
  const [dateFilter, setDateFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const adminData = JSON.parse(localStorage.getItem("adminAuth"));

  const fetchOrders = async () => {
    try {
      const payload = {
        phone: adminData?.phone,
        otp: adminData?.otp,
      };

      const response = await getHotDealsOrderData(payload);

      if (response?.is_success) {
        const normalizedOrders = (response.data || []).map((item) => ({
          ...item,
          order_status: item.order_status?.trim().toUpperCase(),
          payment_status: item.payment_status?.trim().toUpperCase(),
        }));

        const sortedOrders = normalizedOrders.sort(
          (a, b) => new Date(b.order_date) - new Date(a.order_date),
        );

        setOrders(sortedOrders);
        setFilteredOrders(sortedOrders);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let updatedOrders = [...orders];

    // DATE FILTER
    if (dateFilter) {
      updatedOrders = updatedOrders.filter((item) => {
        const orderDate = new Date(item.order_date).toISOString().split("T")[0];

        return orderDate === dateFilter;
      });
    }

    // PAYMENT FILTER
    if (paymentFilter) {
      updatedOrders = updatedOrders.filter(
        (item) =>
          item.payment_status?.trim().toUpperCase() ===
          paymentFilter.trim().toUpperCase(),
      );
    }

    // STATUS FILTER
    if (statusFilter) {
      updatedOrders = updatedOrders.filter(
        (item) =>
          item.order_status?.trim().toUpperCase() ===
          statusFilter.trim().toUpperCase(),
      );
    }

    // LATEST FIRST
    updatedOrders.sort(
      (a, b) => new Date(b.order_date) - new Date(a.order_date),
    );

    setFilteredOrders(updatedOrders);
  }, [orders, dateFilter, paymentFilter, statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleStatusChange = async (item, value) => {
    if (item.order_status === value) return;

    setSelectedOrder(item);
    setSelectedStatus(value);
    setShowModal(true);
  };

  const confirmStatusUpdate = async () => {
    try {
      setUpdating(true);

      const payload = {
        order_id: selectedOrder.order_id,
        status: selectedStatus,
      };

      const response = await updateHotDealsOrderData(payload);

      if (response?.is_success) {
        const updatedOrders = orders.map((item) =>
          item.hotdeal_orders_id === selectedOrder.hotdeal_orders_id
            ? {
                ...item,
                order_status: selectedStatus.trim().toUpperCase(),
              }
            : {
                ...item,
                order_status: item.order_status?.trim().toUpperCase(),
              },
        );

        setOrders(updatedOrders);

        setShowModal(false);
        setSelectedOrder(null);
        setSelectedStatus("");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className={style.loaderWrapper}>
        <p>Loading Orders...</p>
      </div>
    );
  }

  return (
    <>
      {/* <Header /> */}

      <div className={style.pageWrapper}>
        <div className={style.header}>
          <h2>Hot Deals Orders</h2>
          <div className={style.filterWrapper}>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className={style.filterInput}
            />

            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className={style.filterInput}
            >
              <option value="">Payment Status</option>
              <option value="PENDING">PENDING</option>
              <option value="SUCCESS">SUCCESS</option>
              <option value="FAILED">FAILED</option>
               <option value="REFUNDED">REFUNDED</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={style.filterInput}
            >
              <option value="">Order Status</option>

              {statusOptions.map((status) => (
                <option key={status} value={status.trim().toUpperCase()}>
                  {status}
                </option>
              ))}
            </select>

            <button
              className={style.clearBtn}
              onClick={() => {
                setDateFilter("");
                setPaymentFilter("");
                setStatusFilter("");
              }}
            >
              Clear
            </button>
          </div>
        </div>

        <div className={style.ordersGrid}>
          {filteredOrders.map((item) => (
            <div className={style.orderCard} key={item.hotdeal_orders_id}>
              <div className={style.cardTop}>
                <h3>{item.product_name}</h3>

                <span className={style.price}>₹{item.total_amount}</span>
              </div>

              <div className={style.details}>
                <p>
                  <span>Order ID:</span> {item.order_id}
                </p>

                <p>
                  <span>Quantity:</span> {item.quantity}
                </p>

                <p>
                  <span>Payment:</span> {item.payment_status}
                </p>

                <p>
                  <span>Order Date:</span>{" "}
                  {new Date(item.order_date).toLocaleDateString()}
                </p>
              </div>

              <div className={style.customerCompact}>
                <p>
                  <span>{item.name}</span> • {item.phone}
                </p>

                <p className={style.addressText}>
                  {item.flat},{" "}
                  {item.landmark === "NA" ? "" : `${item.landmark}, `}
                  {item.area}, {item.city}, {item.state} - {item.pincode}
                </p>
              </div>

              <div className={style.currentStatus}>
                Current Status : <span>{item.order_status}</span>
              </div>

              <div className={style.statusSection}>
                <label>Update Status</label>

                <select
                  value={item.order_status?.trim().toUpperCase()}
                  onChange={(e) => handleStatusChange(item, e.target.value)}
                  className={style.select}
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status.trim().toUpperCase()}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
        {showModal && (
          <div className={style.modalOverlay}>
            <div className={style.modalBox}>
              <h3>Confirm Status Update</h3>

              <p>
                Change order status from
                <span> {selectedOrder?.order_status} </span>
                to
                <span> {selectedStatus}</span> ?
              </p>

              <div className={style.modalActions}>
                <button
                  className={style.cancelBtn}
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>

                <button
                  className={style.confirmBtn}
                  onClick={confirmStatusUpdate}
                  disabled={updating}
                >
                  {updating ? "Updating..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AdminDashboard;
